import { ArrowLeftOutlined } from '@ant-design/icons'
import { ConfigProvider, Form, message } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { addBrandDataSource, addDataSource, addGA4DataSource, addUploadDataSrc, fetchCryptDataSourceId, fetchDefaultSchema, fetchFirstPartyType, fetchLineType, fetchMappingKeys, fetchSiteType, fetchSources, fetchTrackType, modBrandDataSource, modDataSource, modUploadDataSrc } from '../../apis/source'
import BasicSetting from './components/BasicSetting'
import TableSetting from './components/TableSetting'
import { SnapshotExtraSchemas, Sources } from '../../constants/Sources'
import { BackButton, BasicSettingWrapper, CustomSettingWrapper, SettingWrapper, SubTitle } from './dataSource.styled'
import GA4extras from './components/GA4'

function DataSourceForm (props) {
  const { goBackPath, addMode, editMode } = props
  const { t: ln_t } = useTranslation()
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const user = useSelector((state) => state.user)
  const { groups, nowGroup } = user || {}

  const [sources, setSources] = useState([])
  const [mappingOptions, setMappingOptions] = useState([])
  const [tables, setTables] = useState([])
  const [tableData, setTableData] = useState({})
  const [typeOptions, setTypeOptions] = useState([])
  const [loading, setLoading] = useState(false)

  const goBack = () => navigate(goBackPath)

  const handleAddDataSourceBrand = async (id, brandIds) => {
    if (brandIds && brandIds?.length) {
      const addBrand = await addBrandDataSource({ dataSourceId: id, brandIds })
      if (addBrand?.status !== 200) return setLoading(false)
    }
  }

  const handleAddGA4 = async (formatted, brandIds, uploadFile, raw) => {
    setLoading(true)
    const { name, groupId, type, dataSourceSchemas } = formatted
    const { detailId, analyticsPropertyId, bucketName, upload } = raw
    const reqBody = { name, groupId, type, dataSourceSchemas: JSON.stringify(dataSourceSchemas), detailId, analyticsPropertyId, bucketName, file: upload?.[0].originFileObj }
    const res = await addGA4DataSource(reqBody)
    if (res?.status !== 200) return setLoading(false)
    await handleAddDataSourceBrand(res?.data?.id, brandIds)
    message.success(ln_t('message.success.create'))
    setLoading(false)
    goBack()
  }

  const defParams = { maxResults: 999, groupId: nowGroup, status: 1 }
  const { SITE, TRACK, LINE, FIRST_PARTY, GA4 } = Sources
  const conf = {
    [SITE]: {
      typeApi: () => fetchSiteType({ ...defParams }).then(res => ({ ...res, data: res?.data?.map(i => ({ ...i, type: i.siteName })) })),
      typeLabel: SITE,
      schemaApi: () => fetchDefaultSchema('site')
    },
    [TRACK]: {
      typeApi: () => fetchTrackType({ ...defParams }).then(res => ({ ...res, data: res?.data?.map(i => ({ ...i, type: i.name })) })),
      typeLabel: TRACK,
      schemaApi: () => fetchDefaultSchema('track')
    },
    [LINE]: { typeApi: () => fetchLineType({ ...defParams }), schemaApi: type => fetchDefaultSchema(`line_${type}`), fetchDependsOnType: true },
    [FIRST_PARTY]: { typeApi: () => fetchFirstPartyType({ ...defParams }), useIncremental: true, changeableType: true },
    [GA4]: {
      schemaApi: type => fetchDefaultSchema('ga4'),
      useType: false,
      extras: props => <GA4extras {...props} />,
      handleAdd: handleAddGA4
    }
  }

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`dataSource.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`dataSource.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  // ----- handle save ----- //
  const handleAdd = async (reqBody, brandIds, uploadFile) => {
    setLoading(true)
    const res = await addDataSource(reqBody)
    if (res?.status !== 200) return setLoading(false)
    const { id } = res?.data
    await handleAddDataSourceBrand(id, brandIds)
    await addUploadDataSrc(id, { file: uploadFile })
    message.success(ln_t('message.success.create'))
    setLoading(false)
    goBack()
  }
  const handleEdit = async ({ name, dataSourceSchemas }, brandIds = [], uploadFile) => {
    const res = await modDataSource(data.id, { name, dataSourceSchemas })
    if (res?.status !== 200) return setLoading(false)

    const oldBrand = data?.brands?.map(i => i.id) || []
    const anyChangesInBrand = brandIds.length !== oldBrand.length || brandIds.some(i => !oldBrand.includes(i))
    if (anyChangesInBrand) {
      const modBrand = await modBrandDataSource({ dataSourceId: data.id, brandIds })
      if (modBrand?.status !== 200) return setLoading(false)
    }
    if (uploadFile) await modUploadDataSrc(data.id, { file: uploadFile })
    message.success(ln_t('message.success.modify'))
    setLoading(false)
    goBack()
  }

  const checkCompleteness = () => {
    const checkData = Object.values(tableData)?.flat() || []
    const anyColWithNoType = checkData.some(({ type }) => !type)
    if (anyColWithNoType) return { complete: false, msg: message.error(ln_t('message.error.mustSpecifyType')) }
    const anyMappingWithNoValue = checkData.some(({ mappingKey, mappingKeyId }) => mappingKey && !mappingKeyId)
    if (anyMappingWithNoValue) return { complete: false, msg: message.error(ln_t('message.error.MappingMustHaveValue')) }
    const anyMappingWithNoAlias = checkData.some(({ alias }) => !alias)
    if (anyMappingWithNoAlias) return { complete: false, msg: message.error(ln_t('message.error.mustHaveAlias')) }
    return { complete: true }
  }
  const handleSave = async value => {
    const { name, source, type, isIncremental, incrementColumn, incrementalType = 'append', brands, upload } = value
    const { complete, msg } = checkCompleteness()
    if (!complete) return msg

    const schema = Object.keys(tableData)?.map(key => {
      const tableName = source === FIRST_PARTY ? 'First_Party' : key
      return tableData[key].map(({ columnName, type, isPII, mappingKey, mappingKeyId, alias }) => ({
        tableName, columnName, type, isPII: !isPII ? 0 : 1, mappingKeyId: mappingKey ? mappingKeyId : undefined, alias
      }))
    })?.flat()
    const checkSchema = schema?.some(i => i.columnName === 'm' || i.columnName === 'd')
    if (incrementalType === 'snapshot' && checkSchema) return message.error(ln_t('message.error.dOrMColumnNotAllowed'))

    const reqBody = {
      name,
      type: sources?.find(i => i.name === source)?.id,
      groupId: nowGroup,
      detailId: type,
      isIncremental,
      incrementColumn: source === SITE || source === TRACK || source === LINE ? 'd' : incrementColumn,
      incrementalType,
      dataSourceSchemas: [...schema, ...incrementalType === 'snapshot' ? SnapshotExtraSchemas : []]
    }
    const uploadFile = upload?.[0].status === 'done' ? upload[0].originFileObj : undefined
    const onAdd = conf[source]?.handleAdd || handleAdd
    addMode ? onAdd(reqBody, brands, uploadFile, value) : handleEdit(reqBody, brands, uploadFile)
  }

  const fetchApiId = async () => {
    const res = await fetchCryptDataSourceId(data?.id)
    if (res?.status === 200) form.setFieldValue('apiID', res?.data?.result)
  }
  const initFetch = async () => {
    const res = await fetchMappingKeys({ maxResults: 999, groupIds: `0,${nowGroup}`, status: 1 })
    if (res?.status === 200) setMappingOptions(res?.data)
    const availableSrc = groups?.find(i => i.id === nowGroup)?.dataSourceTypes?.split(',') || []
    const src = await fetchSources({ maxResults: 999 })
    if (src?.status === 200) {
      const type = src?.data?.filter(i => i.type === 'own' && availableSrc.includes(`${i.id}`))
      setSources(type)
    }
  }
  useEffect(() => {
    if ((!addMode && !editMode) || (editMode && !data)) return navigate(goBackPath)
    initFetch()
    if (addMode) return
    const { dataSourceType, detailId, brands, detailName, dataSourceSchemas, ...other } = data
    if (detailName === 'API') fetchApiId()
    form.setFieldsValue({ ...other, source: dataSourceType?.name, type: detailId, brands: brands?.map(i => i.id) })
  }, [])

  const passProps = {
    form,
    goBack,
    mappingOptions,
    defPlaceholder,
    defConfig,
    sources,
    editMode,
    tables,
    setTables,
    conf,
    loading,
    defParams,
    tableData,
    setTableData,
    typeOptions,
    setTypeOptions
  }

  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={() => navigate(goBackPath)} />
        {ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} {ln_t('sidebar.dataSource')}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)}>
          <SettingWrapper>
            <BasicSettingWrapper>
              <SubTitle>{ln_t('util.basicSetting')}</SubTitle>
              <BasicSetting {...passProps} />
            </BasicSettingWrapper>
            <CustomSettingWrapper>
              <SubTitle>{ln_t('sidebar.dataSource')} {ln_t('util.setting')}</SubTitle>
              <TableSetting {...passProps} />
            </CustomSettingWrapper>
          </SettingWrapper>
        </Form>

      </ConfigProvider>
    </>
  )
}

export default DataSourceForm
